import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import { collection, onSnapshot } from 'firebase/firestore';
import { getProducts, Product, StripePayments, Subscription } from '@stripe/firestore-stripe-payments';
import { Dropdown, registerIcons } from '@fluentui/react';

import { firestore } from '../firebase';
import { InvoiceIcon } from './InvoiceIcon';

registerIcons({
  icons: {
    chevrondown: <i className='fas fa-chevron-down' />,
    contact: <i className='fas fa-user' />
  }
});

interface Invoice {
  id: string;
  created: number;
  hosted_invoice_url: string;
  invoice_pdf: string;
  period_start: number;
  period_end: number;
  status: string;
  amount_paid: number;
  amount_due: number;
  amount_remaining: number;
}

export const InvoicesList = ({
  payments,
  uid,
  subscriptions
}: {
  payments: StripePayments;
  uid: string;
  subscriptions: Subscription[];
}) => {
  const { t, i18n } = useTranslation();
  const alert = useAlert();

  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string>();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments]);

  useEffect(() => {
    setSelectedSubscriptionId(subscriptions[subscriptions.length - 1]?.id);
  }, [subscriptions]);

  useEffect(() => {
    if (selectedSubscriptionId) {
      const unsubscribe = onSnapshot(
        collection(firestore, 'customers', uid, 'subscriptions', selectedSubscriptionId, 'invoices'),
        (snapshot) => {
          setInvoices(snapshot.docs.map((doc) => doc.data() as Invoice));
        },
        (error) => {
          setInvoices([]);
          alert.error(error.message);
        }
      );
      return () => unsubscribe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubscriptionId, uid]);

  const loadProducts = async () => {
    const products = await getProducts(payments, {
      includePrices: true,
      activeOnly: true
    });
    setProducts(products);
  };

  const getSubscriptionText = (subscription: Subscription) => {
    const product = products.find((p) => p.id === subscription.product);
    if (product) {
      return t('stripe.select-subscription', {
        name: product.name,
        from: new Date(subscription.current_period_start).toLocaleDateString(i18n.language),
        to: new Date(subscription.current_period_end).toLocaleDateString(i18n.language),
        created: new Date(subscription.created).toLocaleDateString(i18n.language)
      });
    }
    return new Date(subscription.created).toLocaleDateString(i18n.language);
  };

  return (
    <div>
      {subscriptions.length > 1 && (
        <Dropdown
          options={subscriptions.map((subscription) => ({
            key: subscription.id,
            text: getSubscriptionText(subscription)
          }))}
          selectedKey={selectedSubscriptionId}
          onChange={(_ev, item) => item && setSelectedSubscriptionId(item.key as string)}
          className='mb-3'
        />
      )}
      {invoices.length > 0 && (
        <ul className='border-bottom border-secondary list-unstyled'>
          {invoices
            .sort((a, b) => b.created - a.created)
            .map((invoice) => (
              <li key={invoice.id} className='align-items-center d-flex my-1'>
                <InvoiceIcon invoice={invoice} />
                <span className='flex-grow-1 text-start text-truncate'>
                  {t('stripe.invoice', {
                    created: new Date(invoice.created * 1000).toLocaleDateString(i18n.language)
                  })}
                </span>
                <a href={invoice.hosted_invoice_url} target='_blank' rel='noreferrer' className='btn btn-link'>
                  {t('stripe.show-invoice')}
                </a>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};
